import { DocumentIcon, XMarkIcon } from "@heroicons/react/20/solid";
import { useRef, useState } from "react";
import { cn } from "lib/cn";

type FileInputProps = {
    disabled?: boolean;
    filename?: string;
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    handleClear: () => void;
    accept?: string;
};

export const FileInput = ({
    disabled,
    filename,
    onChange,
    handleClear,
    accept = "*",
}: FileInputProps) => {
    const [dragActive, setDragActive] = useState(false);
    const inputRef = useRef<HTMLInputElement | null>(null);

    const handleDrop = (e: React.DragEvent<HTMLLabelElement>) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);
        if (e.dataTransfer.files?.length) {
            const fileEvent = {
                target: {
                    files: e.dataTransfer.files,
                    name: inputRef.current?.name || "file",
                },
            } as unknown as React.ChangeEvent<HTMLInputElement>;
            onChange(fileEvent);
        }
    };

    const handleDrag = (e: React.DragEvent<HTMLLabelElement>) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === "dragenter" || e.type === "dragover") {
            setDragActive(true);
        } else if (e.type === "dragleave") {
            setDragActive(false);
        }
    };

    return (
        <div className="w-full">
            {filename ? (
                <div className="flex items-center justify-between rounded-lg border border-[#c8c8c8] border-gray-300 bg-gray-100 px-4 py-3 text-sm text-gray-700">
                    <span className="truncate">{filename}</span>
                    <button
                        type="button"
                        className="ml-4 text-gray-500 hover:text-red-600"
                        onClick={handleClear}
                        disabled={disabled}
                        aria-label="Remove file"
                    >
                        <XMarkIcon className="h-5 w-5" />
                    </button>
                </div>
            ) : (
                <label
                    htmlFor="file-upload"
                    onDragEnter={handleDrag}
                    onDragLeave={handleDrag}
                    onDragOver={handleDrag}
                    onDrop={handleDrop}
                    className={cn(
                        "flex w-full flex-row items-center justify-center rounded-lg border-2 border-dashed border-[#424242] bg-white px-4 py-3 text-center text-sm text-gray-500 transition hover:border-gray-200",
                        dragActive && "border-blue-500 bg-blue-50",
                        disabled && "cursor-not-allowed opacity-50",
                    )}
                >
                    Drag and drop files here or{" "}
                    <span className="ml-1 text-blue-600 underline">choose file</span>
                    <input
                        id="file-upload"
                        type="file"
                        accept={accept}
                        disabled={disabled}
                        onChange={onChange}
                        ref={inputRef}
                        className="hidden"
                    />
                </label>
            )}
        </div>
    );
};
